/**
 * Macht Dokumentnennungen in einer gerenderten Antwort klickbar.
 *
 * Ein rehype-Schritt nach `rehype-raw`: Jeder Textknoten wird nach Namen der Konversationsdokumente
 * durchsucht (`findDocumentMentions`), und jede Nennung wird zu einem
 * `<span class="doc-cite" data-file-id data-page>`, den `ChatMessage` als Knopf
 * zeichnet. Der Text selbst bleibt, wie er ist — Excel, Termine und Orte lesen die Antwort wörtlich.
 *
 * Ohne `unist-util-visit`, das nur transitiv installiert ist: Der Baum wird hier selbst begangen.
 */
import { ConversationDocument, findDocumentMentions } from './documentMentions';

type HastNode = {
    type: string;
    tagName?: string;
    value?: string;
    properties?: Record<string, unknown>;
    children?: HastNode[];
};

/** Hier wird nichts verlinkt: Ein Link im Link ist ungültig, Code ist Code. */
const SKIP = new Set(['a', 'code', 'pre', 'button', 'script', 'style']);

export const CITATION_CLASS = 'doc-cite';

/**
 * Elemente, an denen ein Satz endet: Hinter ihnen gehört nichts mehr zur Nennung davor. Eine
 * Seite in der nächsten Tabellenzelle ist eine andere Spalte, keine Seitenangabe.
 */
const BLOCK = new Set([
    'p', 'div', 'li', 'ul', 'ol', 'td', 'th', 'tr', 'table', 'thead', 'tbody', 'br', 'hr',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'section', 'article', 'details', 'summary',
]);

/** Wie viel Text hinter einem Knoten gelesen wird — genug für „, S. 1234". */
const LOOKAHEAD = 60;

/**
 * Der Text, der im selben Absatz auf jeden Textknoten folgt — in Dokumentreihenfolge, über
 * Elementgrenzen hinweg (`**X.pdf**, S. 3`), aber nie über eine Blockgrenze.
 */
const followingTexts = (tree: HastNode): Map<HastNode, string> => {
    const sequence: Array<{ node: HastNode; block: number }> = [];
    let block = 0;
    const visit = (node: HastNode) => {
        for (const child of node.children ?? []) {
            if (child.type === 'text') {
                sequence.push({ node: child, block });
            } else if (child.type === 'element') {
                const isBlock = BLOCK.has(String(child.tagName));
                if (isBlock) block++;
                visit(child);
                if (isBlock) block++;
            }
        }
    };
    visit(tree);
    const following = new Map<HastNode, string>();
    sequence.forEach((entry, i) => {
        let text = '';
        for (let j = i + 1; j < sequence.length && sequence[j].block === entry.block && text.length < LOOKAHEAD; j++) {
            text += sequence[j].node.value ?? '';
        }
        following.set(entry.node, text.slice(0, LOOKAHEAD));
    });
    return following;
};

export const rehypeDocumentCitations = (options: { documents: ConversationDocument[] }) => {
    const documents = options?.documents ?? [];
    return (tree: HastNode) => {
        if (!documents.length) return;
        const following = followingTexts(tree);

        const walk = (node: HastNode) => {
            if (!node.children) return;
            const next: HastNode[] = [];
            for (const child of node.children) {
                if (child.type === 'element') {
                    if (SKIP.has(String(child.tagName))) {
                        next.push(child);
                        continue;
                    }
                    walk(child);
                    next.push(child);
                    continue;
                }
                if (child.type !== 'text' || !child.value) {
                    next.push(child);
                    continue;
                }
                const text = child.value;
                const mentions = findDocumentMentions(text, documents, following.get(child) ?? '');
                if (!mentions.length) {
                    next.push(child);
                    continue;
                }
                let at = 0;
                for (const mention of mentions) {
                    if (mention.start > at) next.push({ type: 'text', value: text.slice(at, mention.start) });
                    next.push({
                        type: 'element',
                        tagName: 'span',
                        properties: {
                            className: [CITATION_CLASS],
                            dataFileId: mention.document.fileId,
                            dataFileName: mention.document.filename,
                            ...(mention.page ? { dataPage: String(mention.page) } : {}),
                        },
                        children: [{ type: 'text', value: text.slice(mention.start, mention.end) }],
                    });
                    at = mention.end;
                }
                if (at < text.length) next.push({ type: 'text', value: text.slice(at) });
            }
            node.children = next;
        };

        walk(tree);
    };
};
